package com.yendo.mx.repository

import com.yendo.mx.models.AlgoliaResponse
import com.yendo.mx.ui.search.ResultadoBusqueda
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

import kotlinx.serialization.json.jsonArray
import com.yendo.mx.utils.getAhora
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import com.yendo.mx.models.RestauranteFirebase

// 🚀 OBJETO DE PETICIÓN SEGURO
@Serializable
data class AlgoliaQueryRequest(
    val params: String // 🚀 CORRECCIÓN: Algolia espera "params", no "query" en peticiones REST POST
)

object AlgoliaRepository {
    // ⚠️ TUS LLAVES REALES
    private const val APP_ID = "5IVKYSRJ0B"
    private const val SEARCH_API_KEY = "569f838f576b0722b39259cd86b1413b"

    private const val BASE_URL = "https://$APP_ID-dsn.algolia.net/1/indexes"

    // 🚀 CLIENTE KTOR LIMPIO (SIN HTTPTIMEOUT)
    private val client = HttpClient {
        install(ContentNegotiation) {
            // Explicitamos el uso de kotlinx.serialization
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
                encodeDefaults = true
            })
        }
    }

    // 🚀 CACHÉ EN MEMORIA LRU PARA RESPUESTA INSTANTÁNEA (0ms)
    private val cacheBusquedasAlgolia = mutableMapOf<String, List<ResultadoBusqueda>>()

    suspend fun buscarEnAlgolia(
        query: String, 
        idZona: String, 
        idRestaurante: String = "", // 🚀 FILTRO OPCIONAL POR LOCAL
        tipoNegocio: String = "", // 🚀 FILTRO OPCIONAL "RESTAURANTE" O "COMERCIO"
        page: Int = 0,
        hitsPerPage: Int = 20
    ): List<ResultadoBusqueda> {
        val claveCache = "${query.trim().lowercase()}_${idZona}_${idRestaurante}_${tipoNegocio}_${page}"
        val cached = cacheBusquedasAlgolia[claveCache]
        if (cached != null) {
            println("YENDO_LOG ALGOLIA: ⚡ RESPUESTA INSTANTÁNEA DESDE CACHÉ (0ms) para '$query'")
            return cached
        }

        val resultados = mutableListOf<ResultadoBusqueda>()
        
        // 🚀 Algolia requiere que el query vaya dentro de "params" y esté URL-Encoded
        val encodedQuery = query.encodeURLParameter()
        
        // 🚀 FILTROS DE ZONA TOLERANTES A MAYÚSCULAS/MINÚSCULAS
        val zLower = idZona.lowercase().trim()
        val zUpper = idZona.uppercase().trim()
        val zCap = if (zLower.isNotBlank()) zLower.replaceFirstChar { it.uppercase() } else ""

        var filterStr = if (idZona.isNotBlank()) {
            "(id_zona:'$zLower' OR id_zona:'$zUpper' OR id_zona:'$zCap' OR ubicacion.id_zona:'$zLower' OR ubicacion.id_zona:'$zUpper' OR ubicacion.id_zona:'$zCap')"
        } else ""

        // 🚀 APLICAR FILTRO DE TIPO DE NEGOCIO SI SE PROVEE (RESTAURANTE / COMERCIO)
        if (tipoNegocio.isNotEmpty()) {
            val tipoUpper = tipoNegocio.uppercase().trim()
            val tipoFilter = "(info_publica.tipo_negocio:'$tipoUpper' OR tipo_negocio:'$tipoUpper' OR info_publica.tipoNegocio:'$tipoUpper' OR tipoNegocio:'$tipoUpper')"
            filterStr = if (filterStr.isNotBlank()) "$filterStr AND $tipoFilter" else tipoFilter
        }

        // 🚀 APLICAR FILTRO DE RESTAURANTE SI SE PROVEE
        if (idRestaurante.isNotEmpty()) {
            val restFilter = "(id_restaurante:'$idRestaurante' OR idRestaurante:'$idRestaurante')"
            filterStr = if (filterStr.isNotBlank()) "$filterStr AND $restFilter" else restFilter
        }
        
        // 🚀 ADUANA DE JERARQUÍA (Visible AND ActivoComercio AND NoBloqueado AND NoPausado)
        val ahora = getAhora()
        val filterJerarquia = if (filterStr.isNotBlank()) {
            " AND operacion.visible:true AND operacion.activo_comercio:true AND operacion.bloqueado_por_admin:false AND (operacion.pausado_manualmente:false OR operacion.pausa_hasta < $ahora)"
        } else {
            "operacion.visible:true AND operacion.activo_comercio:true AND operacion.bloqueado_por_admin:false AND (operacion.pausado_manualmente:false OR operacion.pausa_hasta < $ahora)"
        }
        
        val paramsRestaurantes = if (filterStr.isNotBlank()) {
            "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page&filters=${(filterStr + filterJerarquia).encodeURLParameter()}"
        } else {
            "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page&filters=${filterJerarquia.encodeURLParameter()}"
        }
        
        // 🚀 ADUANA PARA PRODUCTOS
        val paramsProductos = if (filterStr.isNotBlank()) {
            "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page&filters=${filterStr.encodeURLParameter()}"
        } else {
            "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page"
        }
        
        println("YENDO_LOG ALGOLIA: 🔍 BUSCANDO (Pag $page): $query (Zone: $idZona, Rest: $idRestaurante)")

        try {
            // 1. BUSCAR EN NEGOCIOS (Índice yendo_negocios)
            var resRestaurantes: AlgoliaResponse = try {
                client.post("$BASE_URL/yendo_negocios/query") {
                    headers {
                        append("X-Algolia-Application-Id", APP_ID)
                        append("X-Algolia-API-Key", SEARCH_API_KEY)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(AlgoliaQueryRequest(params = paramsRestaurantes))
                }.body()
            } catch (e: Exception) {
                AlgoliaResponse(hits = emptyList())
            }
            
            // 🚀 FALLBACK AUTOMÁTICO: Si el filtro estricto de jerarquía devuelve 0 hits (por falta de faceting o atributos vacíos en Algolia)
            if (resRestaurantes.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 1 estricto devolvió 0 hits. Reintentando búsqueda de negocios con filtro básico...")
                val paramsRestaurantesBasico = if (filterStr.isNotBlank()) "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page&filters=${filterStr.encodeURLParameter()}" else "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page"
                resRestaurantes = try {
                    client.post("$BASE_URL/yendo_negocios/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsRestaurantesBasico))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            // 🚀 FALLBACK 2: Búsqueda libre en yendo_negocios
            if (resRestaurantes.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 2 devolvió 0 hits. Reintentando búsqueda libre en yendo_negocios...")
                val paramsRestaurantesLibre = "query=$encodedQuery&hitsPerPage=$hitsPerPage&page=$page"
                resRestaurantes = try {
                    client.post("$BASE_URL/yendo_negocios/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsRestaurantesLibre))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            println("YENDO_LOG ALGOLIA: ✅ HITS RESTAURANTES (FINAL): ${resRestaurantes.hits.size}")

            resRestaurantes.hits.forEach { hit ->
                try {
                    val id = hit["objectID"]?.jsonPrimitive?.content ?: ""
                    val infoPublica = hit["info_publica"]?.jsonObject

                    // 🚀 BÚSQUEDA ROBUSTA (Nested o Flattened)
                    val nombre = infoPublica?.get("nombre")?.jsonPrimitive?.content 
                        ?: hit["info_publica.nombre"]?.jsonPrimitive?.content
                        ?: hit["nombre"]?.jsonPrimitive?.content
                        ?: "Negocio"
                        
                    val logo = infoPublica?.get("logotipo")?.jsonPrimitive?.content 
                        ?: hit["info_publica.logotipo"]?.jsonPrimitive?.content
                        ?: hit["logotipo"]?.jsonPrimitive?.content
                        ?: ""

                    // 🚀 EXTRACCIÓN EXHAUSTIVA DE SUB-CATEGORÍAS (Busca en varios niveles)
                    val subcatsArray = infoPublica?.get("subcategorias")?.jsonArray
                        ?: hit["info_publica.subcategorias"]?.jsonArray
                        ?: hit["subcategorias"]?.jsonArray
                        
                    val subcatsString = subcatsArray?.mapNotNull { it.jsonPrimitive.content }?.joinToString(", ") ?: ""
                    
                    val categoriaPrincipal = infoPublica?.get("categoria")?.jsonPrimitive?.content
                        ?: hit["info_publica.categoria"]?.jsonPrimitive?.content
                        ?: hit["categoria"]?.jsonPrimitive?.content
                        ?: "Restaurante"
                        
                    val descripcion = if (subcatsString.isNotBlank()) subcatsString else categoriaPrincipal

                    resultados.add(ResultadoBusqueda(id, nombre, descripcion, logo, "NEGOCIO"))
                } catch (e: Exception) {
                    // Ignorar documentos mal formados silenciosamente
                }
            }

            // 2. BUSCAR EN PRODUCTOS
            val resProductos: AlgoliaResponse = client.post("$BASE_URL/yendo_productos/query") {
                headers {
                    append("X-Algolia-Application-Id", APP_ID)
                    append("X-Algolia-API-Key", SEARCH_API_KEY)
                }
                contentType(ContentType.Application.Json)
                setBody(AlgoliaQueryRequest(params = paramsProductos))
            }.body()
            
            println("YENDO_LOG ALGOLIA: ✅ HITS PRODUCTOS: ${resProductos.hits.size}")

            resProductos.hits.forEach { hit ->
                try {
                    val id = hit["objectID"]?.jsonPrimitive?.content ?: ""
                    val infoProducto = hit["info_producto"]?.jsonObject

                    // 🚀 BÚSQUEDA ROBUSTA (Nested o Flattened)
                    val nombre = infoProducto?.get("nombre")?.jsonPrimitive?.content
                        ?: hit["info_producto.nombre"]?.jsonPrimitive?.content
                        ?: hit["nombre"]?.jsonPrimitive?.content
                        ?: "Producto"

                    val marca = infoProducto?.get("marca")?.jsonPrimitive?.content
                        ?: hit["info_producto.marca"]?.jsonPrimitive?.content
                        ?: hit["marca"]?.jsonPrimitive?.content
                        ?: ""
                        
                    val descripcion = infoProducto?.get("descripcion")?.jsonPrimitive?.content
                        ?: hit["info_producto.descripcion"]?.jsonPrimitive?.content
                        ?: hit["descripcion"]?.jsonPrimitive?.content
                        ?: ""
                        
                    val foto = infoProducto?.get("foto")?.jsonPrimitive?.content
                        ?: hit["info_producto.foto"]?.jsonPrimitive?.content
                        ?: hit["foto"]?.jsonPrimitive?.content
                        ?: ""

                    val subtituloBusqueda = when {
                        marca.isNotBlank() && descripcion.isNotBlank() -> "Marca: $marca • $descripcion"
                        marca.isNotBlank() -> "Marca: $marca"
                        else -> descripcion
                    }

                    // 🚀 BÚSQUEDA EXHAUSTIVA DEL ID DEL RESTAURANTE / NEGOCIO (Blindaje anti-nulos)
                    val idRest = hit["id_negocio"]?.jsonPrimitive?.content
                        ?: hit["idNegocio"]?.jsonPrimitive?.content
                        ?: hit["id_restaurante"]?.jsonPrimitive?.content
                        ?: hit["idRestaurante"]?.jsonPrimitive?.content
                        ?: infoProducto?.get("id_negocio")?.jsonPrimitive?.content
                        ?: infoProducto?.get("idNegocio")?.jsonPrimitive?.content
                        ?: infoProducto?.get("id_restaurante")?.jsonPrimitive?.content
                        ?: infoProducto?.get("idRestaurante")?.jsonPrimitive?.content
                        ?: hit["info_producto.id_negocio"]?.jsonPrimitive?.content
                        ?: hit["info_producto.idRestaurante"]?.jsonPrimitive?.content
                        ?: ""

                    resultados.add(ResultadoBusqueda(id, nombre, subtituloBusqueda, foto, "PRODUCTO", idRest))
                } catch (e: Exception) {
                    // Ignorar documentos mal formados silenciosamente
                }
            }

        } catch (e: Exception) {
            println("YENDO_LOG ALGOLIA: 🚨 ERROR KTOR/ALGOLIA: ${e.message}")
            e.printStackTrace()
        }

        if (resultados.isNotEmpty()) {
            if (cacheBusquedasAlgolia.size > 50) {
                cacheBusquedasAlgolia.clear()
            }
            cacheBusquedasAlgolia[claveCache] = resultados
        }

        return resultados
    }

    /**
     * 🚀 BÚSQUEDA OPTIMIZADA DE PRODUCTOS (Para Buscador Interno de Restaurante)
     * Retorna modelos de Producto construidos desde Algolia (Sin costo de Firestore)
     */
    suspend fun buscarProductosEnRestaurante(
        query: String,
        idRestaurante: String,
        idZona: String
    ): List<com.yendo.mx.models.Producto> {
        val encodedQuery = query.encodeURLParameter()

        // 🚀 FILTRO ROBUSTO Y EXHAUSTIVO POR NEGOCIO (id_negocio, id_restaurante, idNegocio, idRestaurante)
        val filterRest = "(id_negocio:'$idRestaurante' OR id_restaurante:'$idRestaurante' OR idNegocio:'$idRestaurante' OR idRestaurante:'$idRestaurante')"

        val zLower = idZona.lowercase().trim()
        val zUpper = idZona.uppercase().trim()
        val zCap = if (zLower.isNotBlank()) zLower.replaceFirstChar { it.uppercase() } else ""

        val filterZona = if (idZona.isNotBlank()) {
            "(id_zona:'$zLower' OR id_zona:'$zUpper' OR id_zona:'$zCap' OR ubicacion.id_zona:'$zLower' OR ubicacion.id_zona:'$zUpper' OR ubicacion.id_zona:'$zCap')"
        } else ""

        val filterStr = if (filterZona.isNotBlank()) "$filterRest AND $filterZona" else filterRest
        val paramsConZona = "query=$encodedQuery&hitsPerPage=50&filters=${filterStr.encodeURLParameter()}"

        println("YENDO_LOG ALGOLIA: 🔍 BUSCANDO PRODUCTOS EN LOCAL: '$query' (Zone: $idZona, Rest: $idRestaurante)")

        return try {
            // 1. Intentar consulta con Filtro de Zona + Negocio
            var res: AlgoliaResponse = try {
                client.post("$BASE_URL/yendo_productos/query") {
                    headers {
                        append("X-Algolia-Application-Id", APP_ID)
                        append("X-Algolia-API-Key", SEARCH_API_KEY)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(AlgoliaQueryRequest(params = paramsConZona))
                }.body()
            } catch (e: Exception) {
                AlgoliaResponse(hits = emptyList())
            }

            // 🚀 FALLBACK 1: Si id_zona no está indexado como Facet en Algolia y devuelve 0 hits, filtrar SOLO por ID de Negocio
            if (res.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 1 con id_zona devolvió 0 hits. Reintentando filtro exclusivo por negocio...")
                val paramsSoloNegocio = "query=$encodedQuery&hitsPerPage=50&filters=${filterRest.encodeURLParameter()}"
                res = try {
                    client.post("$BASE_URL/yendo_productos/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsSoloNegocio))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            // 🚀 FALLBACK 2: Probar índice alternativo 'Yendo_Productos'
            if (res.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 2 devolvió 0 hits. Probando índice alternativo 'Yendo_Productos'...")
                val paramsSoloNegocio = "query=$encodedQuery&hitsPerPage=50&filters=${filterRest.encodeURLParameter()}"
                res = try {
                    client.post("$BASE_URL/Yendo_Productos/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsSoloNegocio))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            // 🚀 FALLBACK 3: Búsqueda libre en 'yendo_productos' sin filtro de faceting
            if (res.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 3 devolvió 0. Probando búsqueda libre en 'yendo_productos'...")
                val paramsLibre = "query=$encodedQuery&hitsPerPage=50"
                res = try {
                    client.post("$BASE_URL/yendo_productos/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsLibre))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            // 🚀 FALLBACK 4: Probar índice 'Productos' libre
            if (res.hits.isEmpty()) {
                println("YENDO_LOG ALGOLIA: ⚠️ Intento 4 devolvió 0. Probando índice 'Productos'...")
                val paramsLibre = "query=$encodedQuery&hitsPerPage=50"
                res = try {
                    client.post("$BASE_URL/Productos/query") {
                        headers {
                            append("X-Algolia-Application-Id", APP_ID)
                            append("X-Algolia-API-Key", SEARCH_API_KEY)
                        }
                        contentType(ContentType.Application.Json)
                        setBody(AlgoliaQueryRequest(params = paramsLibre))
                    }.body()
                } catch (e: Exception) {
                    AlgoliaResponse(hits = emptyList())
                }
            }

            println("YENDO_LOG ALGOLIA: ✅ HITS PRODUCTOS EN LOCAL (FINAL): ${res.hits.size}")

            res.hits.mapNotNull { hit ->
                try {
                    val id = hit["objectID"]?.jsonPrimitive?.content ?: ""
                    val infoProd = hit["info_producto"]?.jsonObject
                    val operacion = hit["operacion"]?.jsonObject

                    com.yendo.mx.models.Producto(
                        id = id,
                        idNegocio = idRestaurante,
                        infoProducto = com.yendo.mx.models.InfoProducto(
                            nombre = infoProd?.get("nombre")?.jsonPrimitive?.content 
                                ?: hit["info_producto.nombre"]?.jsonPrimitive?.content ?: "",
                            marca = infoProd?.get("marca")?.jsonPrimitive?.content 
                                ?: hit["info_producto.marca"]?.jsonPrimitive?.content 
                                ?: hit["marca"]?.jsonPrimitive?.content ?: "",
                            descripcion = infoProd?.get("descripcion")?.jsonPrimitive?.content 
                                ?: hit["info_producto.descripcion"]?.jsonPrimitive?.content ?: "",
                            foto = infoProd?.get("foto")?.jsonPrimitive?.content 
                                ?: hit["info_producto.foto"]?.jsonPrimitive?.content ?: "",
                            precio = hit["precio"]?.jsonPrimitive?.content?.toDoubleOrNull() 
                                ?: hit["info_producto.precio"]?.jsonPrimitive?.content?.toDoubleOrNull() 
                                ?: infoProd?.get("precio")?.jsonPrimitive?.content?.toDoubleOrNull() ?: 0.0,
                            precioPor = hit["precio_por"]?.jsonPrimitive?.content 
                                ?: hit["info_producto.precio_por"]?.jsonPrimitive?.content 
                                ?: infoProd?.get("precio_por")?.jsonPrimitive?.content ?: "UNIDAD",
                            tipoVenta = hit["tipo_venta"]?.jsonPrimitive?.content 
                                ?: hit["info_producto.tipo_venta"]?.jsonPrimitive?.content 
                                ?: infoProd?.get("tipo_venta")?.jsonPrimitive?.content ?: "UNIDAD",
                            pesoEstimadoKg = hit["peso_estimado_kg"]?.jsonPrimitive?.content?.toDoubleOrNull() 
                                ?: hit["info_producto.peso_estimado_kg"]?.jsonPrimitive?.content?.toDoubleOrNull() 
                                ?: infoProd?.get("peso_estimado_kg")?.jsonPrimitive?.content?.toDoubleOrNull() ?: 0.0,
                            precioOriginal = infoProd?.get("precio_original")?.jsonPrimitive?.content?.toDoubleOrNull() 
                                ?: hit["info_producto.precio_original"]?.jsonPrimitive?.content?.toDoubleOrNull() ?: 0.0,
                            tipoPromocion = infoProd?.get("tipo_promocion")?.jsonPrimitive?.content 
                                ?: hit["info_producto.tipo_promocion"]?.jsonPrimitive?.content ?: "NINGUNA"
                        ),
                        operacion = com.yendo.mx.models.OperacionProducto(
                            disponible = operacion?.get("disponible")?.jsonPrimitive?.content?.toBoolean() 
                                ?: hit["operacion.disponible"]?.jsonPrimitive?.content?.toBoolean() ?: true
                        ),
                        // 🚀 MAPEANDO ADICIONALES (Para que el BottomSheet no salga vacío)
                        adicionales = try {
                            hit["adicionales"]?.jsonArray?.let {
                                Json { ignoreUnknownKeys = true }.decodeFromJsonElement(it)
                            } ?: emptyList()
                        } catch (e: Exception) { emptyList() },
                        // 🚀 MAPEANDO ESCALAS DE MAYOREO
                        escalasPrecio = try {
                            hit["escalas_precio"]?.jsonArray?.let {
                                Json { ignoreUnknownKeys = true }.decodeFromJsonElement(it)
                            } ?: emptyList()
                        } catch (e: Exception) { emptyList() }
                    )
                } catch (e: Exception) {
                    null
                }
            }
        } catch (e: Exception) {
            emptyList()
        }
    }
}
